import cx from "classnames";
import React, { ReactNode, useState } from "react";

import { IconButton } from "atoms/icon-button/IconButton";

import ArrowLeft from "../icon/icons/arrow_left.svg";
import ArrowRight from "../icon/icons/arrow_right.svg";

interface Props {
  "className"?: string;
  "slideClassName"?: string;
  "fillHeight"?: boolean;
  "previousLabel"?: string;
  "nextLabel"?: string;
  /** Specify the content of the slides and title */
  "slides": ReactNode[];

  /** Specify the function to be called when the index of the slide changes */
  "onUpdateIndex": (index: number) => void;

  /** Specify the aspect ratio for the carousel slides (e.g., "2/1", "16/9", "1/1"). Defaults to "2/1" */
  "aspectRatio"?: string;

  /** Specify the testid for current component */
  "data-testid"?: string;
}

export const SimpleCarousel: React.FC<Props> = ({
  slides,
  onUpdateIndex,
  className,
  slideClassName,
  fillHeight,
  previousLabel,
  nextLabel,
  aspectRatio = "2/1",
  "data-testid": dataTestId,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const ref = React.useRef<(HTMLDivElement | null)[]>([]);

  const updateIndex = (index: number) => {
    onUpdateIndex(index);
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1;
    updateIndex(newIndex);
    ref.current[newIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };
  const goToNext = () => {
    const isLastSlide = currentIndex === slides.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    updateIndex(newIndex);
    ref.current[newIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  return (
    <div
      className={cx(
        "flex flex-col rounded",
        fillHeight && "min-h-0 flex-1",
        slides.length === 1 && "pb-8",
        className,
      )}
      data-testid={dataTestId}
    >
      <div
        className={cx(
          "flex w-full snap-x snap-mandatory overflow-x-hidden rounded",
          fillHeight && "min-h-0 flex-1",
        )}
        style={fillHeight ? undefined : { aspectRatio }}
      >
        {slides.map((item, index) => (
          <div
            ref={(e) => {
              ref.current[index] = e;
            }}
            key={index}
            className={cx(
              "block h-full snap-center object-fill px-4",
              fillHeight && "w-full shrink-0",
              slideClassName,
            )}
            style={fillHeight ? undefined : { aspectRatio }}
          >
            {item}
          </div>
        ))}
      </div>
      <div
        className={cx(
          "w-fill flex items-center justify-between p-1",
          slides.length === 1 && "hidden",
        )}
      >
        <IconButton
          aria-label={previousLabel}
          svg={ArrowLeft}
          size="small"
          style="iconOnly"
          onClick={goToPrevious}
        />
        <span className="text-body3 tabular-nums" aria-live="polite">
          {currentIndex + 1} / {slides.length}
        </span>
        <IconButton
          aria-label={nextLabel}
          svg={ArrowRight}
          size="small"
          style="iconOnly"
          onClick={goToNext}
        />
      </div>
    </div>
  );
};

export default SimpleCarousel;
